import moment from "moment";
import { mealTypes } from "../../../enums";
import { applyHotelBirthdayPromo } from "../../../common/util/hotelBirthdayPromo";
import { applyEarlyDiscount } from "../../../firestore/firestoreService/roomPriceHelpers";

// Normalizes meal strings to a consistent key for label mapping.
export const normalizeMealKey = (value: string) =>
  value.toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_");

// Resolves a readable meal label using aliases, known labels, or raw value.
export const getMealIncludeLine = (primaryRoom: any) => {
  const rawMealType =
    primaryRoom?.mealType || primaryRoom?.mealsType || primaryRoom?.board || "";
  const mealTypeAliases: Record<string, string> = {
    ROOM_ONLY: "NO_MEAL",
    RO: "NO_MEAL",
    BED_AND_BREAKFAST: "BREAKFAST",
    B_B: "BREAKFAST",
    BB: "BREAKFAST",
    HALF_BOARD: "HALF_BOARD",
    HB: "HALF_BOARD",
    FULL_BOARD: "FULL_BOARD",
    FB: "FULL_BOARD",
    ALL_INCLUSIVE: "ALL_INCLUSIVE",
    AI: "ALL_INCLUSIVE",
  };
  const mealTypeLabelMap: Record<string, string> = {
    NO_MEAL: "Χωρίς πρόγευμα",
    BREAKFAST: "Με πρόγευμα",
    HALF_BOARD: "Ημιδιατροφή",
    FULL_BOARD: "Πλήρης διατροφή",
    ALL_INCLUSIVE: "All Inclusive",
  };
  const normalizedMealKey = rawMealType ? normalizeMealKey(rawMealType) : "";
  const mealKey =
    (normalizedMealKey && mealTypeAliases[normalizedMealKey]) ||
    normalizedMealKey;
  return mealKey && mealTypeLabelMap[mealKey]
    ? mealTypeLabelMap[mealKey]
    : rawMealType && mealTypes[rawMealType]?.label
      ? mealTypes[rawMealType].label
      : rawMealType;
};

// Formats a number as a currency label for hotel prices, rounding up to match
// the hotel details price list and cart display.
export const formatPrice = (value?: number) =>
  value !== undefined && Number.isFinite(value)
    ? new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(Math.ceil(value))
    : "—";

// Returns price labels and a discount flag for the UI.
export const buildPriceLabels = (
  totalPrice?: number,
  discountPrice?: number,
) => {
  const numericTotalPrice = Number(totalPrice);
  const numericDiscountPrice = Number(discountPrice);
  const hasDiscountPrice =
    Number.isFinite(numericDiscountPrice) &&
    numericDiscountPrice > 0 &&
    (!Number.isFinite(numericTotalPrice) ||
      numericDiscountPrice > numericTotalPrice);
  return {
    priceLabel: formatPrice(totalPrice),
    discountPriceLabel: hasDiscountPrice ? formatPrice(discountPrice) : "",
    hasDiscountPrice,
  };
};

// Builds the nights label for the price meta line.
export const buildNightsLabel = (nights?: number) =>
  nights && nights > 0 ? `${nights} ${nights > 1 ? "βράδια" : "βράδυ"}` : "";
// Returns the on-request message based on the room availability state.
export const buildOnRequestMessage = (
  onRequestType?: "full" | "short" | null,
) =>
  onRequestType === "full"
    ? "Το κατάλυμα δεν παρέχει άμεση διαδικτυακή επιβεβαίωση για τις συγκεκριμένες ημερομηνίες. Μπορείτε να μας στείλετε το αίτημα σας χωρίς καμία δέσμευση επιλέγοντας την On Request προσφορά, ή να προχωρήσετε με άμεση κράτηση στις διαθέσιμες προσφορές."
    : onRequestType === "short"
      ? "Το κατάλυμα δεν παρέχει άμεση διαδικτυακή επιβεβαίωση για τις συγκεκριμένες ημερομηνίες. Μπορείτε να μας στείλετε το αίτημα σας χωρίς καμία δέσμευση επιλέγοντας την On Request προσφορά ή προχωρήστε με άμεση κράτηση με την επόμενη διαθέσιμη ημερομηνία που προτείνεται πιο κάτω."
      : "";

// Formats the minimum-stay notice when dates do not meet the stay rules.
export const buildMinStayNotice = (isMinStay?: boolean, minStay?: number) =>
  isMinStay && minStay
    ? `Για τις ημερομηνίες που έχετε επιλέξει, η ελάχιστη διάρκεια διαμονής είναι ${minStay} διανυκτερεύσεις. Ρίξτε μια ματιά στις προτάσεις μας παρακάτω.`
    : "";

// Builds a room availability label for the card.
export const buildRoomCountLine = (availableRoomsCount?: number) =>
  availableRoomsCount && availableRoomsCount > 1
    ? `Διαθέσιμα ${availableRoomsCount} δωμάτια`
    : availableRoomsCount === 1
      ? "Διαθέσιμο 1 δωμάτιο"
      : "";

// Formats the primary CTA label depending on on-request availability.
export const buildCtaLabel = (
  onRequestType?: "full" | "short" | null,
  hasDirectAvailability?: boolean,
) => (onRequestType && !hasDirectAvailability ? "On Request" : "Κράτηση");

// Computes guest counts with fallbacks to filter defaults.
export const buildGuestCounts = (
  guestParams?: {
    adultNumbers: number;
    childrenNumber: number;
    infant: number;
    rooms: number;
    childrenAges?: string | string[];
  },
  filter?: any,
) => ({
  adults: Number(guestParams?.adultNumbers ?? filter?.adultNumbers ?? 2),
  children: Number(guestParams?.childrenNumber ?? filter?.childrenNumber ?? 0),
  infants: Number(guestParams?.infant ?? filter?.infant ?? 0),
});

// Calculates the total price for a suggested date range.
export const calculateTotalForRange = (params: {
  start: moment.Moment;
  end: moment.Moment;
  priceDocs: any[];
  periodMap: Record<string, any>;
  guestCounts: { adults: number; children: number; infants: number };
  normalizedPartnerId?: string;
  hotelId?: string;
}) => {
  const {
    start,
    end,
    priceDocs,
    periodMap,
    guestCounts,
    normalizedPartnerId,
    hotelId,
  } = params;
  const nightsCount = Math.max(end.diff(start, "days"), 1);
  const rangeDates = Array.from({ length: nightsCount }, (_, idx) =>
    start.clone().add(idx, "days").format("YYYY-MM-DD"),
  );
  let total = 0;
  for (const date of rangeDates) {
    const matchingDocs = priceDocs.filter(
      (price: any) =>
        (!price.dateRangeEnd
          ? price.date === date
          : price.date <= date && date <= price.dateRangeEnd) &&
        !price.stopSale &&
        price.status !== false &&
        (price.roomAllotment || 0) > (price.allotment || 0),
    );
    if (!matchingDocs.length) return Number.POSITIVE_INFINITY;
    let bestPrice = Number.POSITIVE_INFINITY;
    for (const priceDoc of matchingDocs) {
      const period = priceDoc.periodId ? periodMap[priceDoc.periodId] : null;
      for (const item of priceDoc.priceList || []) {
        const matchesAdults = Number(item.adults ?? 0) === guestCounts.adults;
        const matchesChildren =
          Number(item.childs ?? 0) === guestCounts.children;
        const infantsValue = item?.infants;
        const matchesInfants =
          infantsValue == null || Number(infantsValue) >= guestCounts.infants;
        if (!matchesAdults || !matchesChildren || !matchesInfants) continue;
        const hasPartnerPrice =
          normalizedPartnerId &&
          item.partnersDiscountPrice &&
          item.partnersDiscountPrice[normalizedPartnerId] != null;
        const itemPrice = hasPartnerPrice
          ? Number(item.partnersDiscountPrice?.[normalizedPartnerId])
          : Number(item.totalPrice);
        if (!Number.isFinite(itemPrice)) continue;
        const discounted = applyEarlyDiscount(itemPrice, period);
        const promoDiscounted = applyHotelBirthdayPromo({
          price: discounted,
          partnerId: normalizedPartnerId?.toUpperCase(),
          hotelId,
        });
        if (promoDiscounted < bestPrice) bestPrice = promoDiscounted;
      }
    }
    if (!Number.isFinite(bestPrice)) return Number.POSITIVE_INFINITY;
    total += bestPrice;
  }
  return total;
};

// Creates suggestion ranges for minimum-stay and on-request cases.
export const buildSuggestions = (priceSummary?: {
  startDate?: string | null;
  endDate?: string | null;
  nights?: number;
  minStay?: number;
  isMinStay?: boolean;
  onRequestType?: "full" | "short" | null;
  hasDirectAvailability?: boolean;
}) => {
  if (!priceSummary?.startDate || !priceSummary?.endDate) return [];
  const shouldShow =
    priceSummary?.isMinStay || priceSummary?.onRequestType === "short";
  if (!shouldShow) return [];
  let minStay = priceSummary.minStay || Math.max(priceSummary.nights || 1, 1);
  const requested = Math.max(priceSummary.nights || minStay, 1);
  if (minStay < requested) {
    minStay = requested;
  }
  const start = moment(priceSummary.startDate);
  const end = moment(priceSummary.endDate);
  const diff = Math.max(minStay - requested, 0);
  const extendSuggestion = {
    key: "extend",
    start,
    end: start.clone().add(minStay, "days"),
    status: priceSummary.onRequestType ? "on-request" : "available",
  };
  const beforeSuggestion =
    diff > 0
      ? {
          key: "before",
          start: start.clone().subtract(diff, "days"),
          end: start.clone().subtract(diff, "days").add(minStay, "days"),
          status: priceSummary.onRequestType ? "on-request" : "available",
        }
      : null;
  const afterSuggestion = {
    key: "after",
    start: end.clone(),
    end: end.clone().add(minStay, "days"),
    status:
      priceSummary.onRequestType && priceSummary.hasDirectAvailability
        ? "available"
        : priceSummary.onRequestType
          ? "on-request"
          : "available",
  };
  const decorateStatus = (
    suggestion: null | {
      key: string;
      start: moment.Moment;
      end: moment.Moment;
      status: string;
    },
    index: number,
  ) => {
    if (!suggestion) return null;
    if (
      priceSummary?.onRequestType &&
      priceSummary?.hasDirectAvailability &&
      index === 0
    ) {
      return { ...suggestion, status: "on-request" };
    }
    return suggestion;
  };
  return [beforeSuggestion, extendSuggestion, afterSuggestion]
    .map(decorateStatus)
    .filter(
      (
        s,
      ): s is {
        key: string;
        start: moment.Moment;
        end: moment.Moment;
        status: string;
      } => !!s && !!s.start && !!s.end,
    );
};

// Controls whether the suggestion section should be displayed.
export const shouldShowSuggestions = (priceSummary?: {
  isMinStay?: boolean;
  onRequestType?: "full" | "short" | null;
}) =>
  Boolean(priceSummary?.isMinStay) || priceSummary?.onRequestType === "short";

// Controls whether the right-side price column should be visible.
export const shouldShowPriceColumn = (priceSummary?: {
  isMinStay?: boolean;
  onRequestType?: "full" | "short" | null;
  hasDirectAvailability?: boolean;
}) =>
  priceSummary?.isMinStay === false &&
  (priceSummary?.onRequestType === "full" || !priceSummary?.onRequestType);

// Builds the search params used for navigation and sharing.
export const buildSearchParams = (params: {
  start?: string | null;
  end?: string | null;
  guestParams?: {
    adultNumbers: number;
    childrenNumber: number;
    infant: number;
    rooms: number;
    childrenAges?: string | string[];
  };
  filter?: any;
  opts?: { onRequest?: boolean };
}) => {
  const { start, end, guestParams, filter, opts } = params;
  const searchParams = new URLSearchParams();
  if (start) searchParams.set("startDate", start);
  if (end) searchParams.set("endDate", end);
  const guests: any = guestParams || {};
  searchParams.set(
    "adultNumbers",
    `${guests.adultNumbers ?? filter?.adultNumbers ?? 2}`,
  );
  searchParams.set(
    "childrenNumber",
    `${guests.childrenNumber ?? filter?.childrenNumber ?? 0}`,
  );
  searchParams.set("infant", `${guests.infant ?? filter?.infant ?? 0}`);
  searchParams.set("rooms", `${guests.rooms ?? filter?.rooms ?? 1}`);
  const childrenAges = guests.childrenAges || filter?.childrenAges;
  if (childrenAges) {
    const agesArray = Array.isArray(childrenAges)
      ? childrenAges
      : `${childrenAges}`.split(",");
    if (agesArray.filter(Boolean).length) {
      searchParams.set("childrenAges", agesArray.filter(Boolean).join(","));
    }
  }
  if (opts?.onRequest) {
    searchParams.set("onRequest", "true");
  }
  return searchParams;
};
